import clsx from 'clsx'
import React, { FunctionComponent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Button } from '../Button'
import { Icon } from '../Icon'
import { useBreakpoint } from '../useBreakpoint'
import { arrayChunks } from '../utils/function'
import { CarouselProps } from './Carousel'
import { CarouselItem } from './CarouselItem'
import styles from './style.module.scss'

export interface CarouselTransformSliderProps extends Omit<CarouselProps, 'onScroll'> {}

export const CarouselTransformSlider: FunctionComponent<CarouselTransformSliderProps> = ({
  children,
  Small = 2,
  Medium = 4,
  Large = 6,
  testNameSpace = '',
  keyNameSpace = '',
  prevButtonAriaLabel = '',
  nextButtonAriaLabel = '',
  onChange,
  alwaysShowTheNextButton = false,
  controlContainerDom,
}) => {
  const { screenSize } = useBreakpoint()
  const [transitionEnd, setTransitionEnd] = useState(true)
  const [currentIndex, setCurrentIndex] = useState(0)

  const childrenArr = React.Children.toArray(children)
  const maxItemsSize = screenSize === 'Large' ? Large : screenSize === 'Medium' ? Medium : screenSize === 'Small' ? Small : Large
  const slides = arrayChunks(childrenArr, maxItemsSize)
  const minimunIndex = 0
  const maximumIndex = slides.length - 1
  const lastSlideSize = slides[maximumIndex].length
  const firstUpdate = useRef(true)

  useEffect(() => {
    if (!firstUpdate.current && typeof onChange !== 'undefined') {
      onChange(currentIndex, maximumIndex)
      return
    }
    firstUpdate.current = false
  }, [currentIndex, onChange, maximumIndex])

  const getTransformListClassName = useCallback(
    (index: number): string => {
      const classNames = clsx(
        styles.transformSlide,
        index === currentIndex && styles.current,
        index > currentIndex && styles.next,
        index < currentIndex && styles.prev,
        transitionEnd && styles.transitionEnd
      )

      // If the last slide has not enough items, we need to align it to right.
      if (lastSlideSize < maxItemsSize && currentIndex === maximumIndex) {
        const classNamesForAlign = clsx(
          index === maximumIndex - 1 && styles.secondLast,
          index === maximumIndex - 1 && styles[`secondLast-${lastSlideSize}-of-${maxItemsSize}`],
          index === maximumIndex && styles[`last-${lastSlideSize}-of-${maxItemsSize}`]
        )
        return `${classNames} ${classNamesForAlign}`
      }

      return classNames
    },
    [currentIndex, maximumIndex, maxItemsSize, lastSlideSize, transitionEnd]
  )

  const renderTransformSlides = useCallback((): ReactNode => {
    const sliderTestNameSpace = testNameSpace.length > 0 ? `${testNameSpace}-carousel-transform-slider` : 'carousel-transform-slider'
    const listKeyPrefix = keyNameSpace.length > 0 ? `${keyNameSpace}-carousel-transform-list` : 'carousel-transform-list'
    const itemKeyPrefix = keyNameSpace.length > 0 ? `${keyNameSpace}-carousel-transform-item` : 'carousel-transform-item'

    return (
      <div className={styles.transformListContainer} data-testid={sliderTestNameSpace}>
        {slides.map((groupedItems, index: number) => (
          <ul className={getTransformListClassName(index)} key={`${listKeyPrefix}-${index}`} onTransitionEnd={() => setTransitionEnd(true)}>
            {groupedItems.map((item, indexItem) => (
              <CarouselItem key={`${itemKeyPrefix}-${indexItem}`} maxItemsSize={maxItemsSize}>
                {item}
              </CarouselItem>
            ))}
          </ul>
        ))}
      </div>
    )
  }, [slides, getTransformListClassName, keyNameSpace, maxItemsSize, testNameSpace])

  const renderActionButtons = useCallback((): ReactNode => {
    const buttonTestNameSpace = testNameSpace.length > 0 ? `${testNameSpace}-carousel` : 'carousel'

    const handleNextClick = (): void => {
      if (currentIndex === maximumIndex) {
        return
      }
      setCurrentIndex(currentIndex + 1)
      setTransitionEnd(false)
    }

    const handlePrevClick = (): void => {
      if (currentIndex === minimunIndex) {
        return
      }
      setCurrentIndex(currentIndex - 1)
      setTransitionEnd(false)
    }

    const showPrevButton = currentIndex > minimunIndex
    const buttonPrevClassnames = clsx(styles.button, styles.buttonPrev, showPrevButton && styles.show)

    const showNextButton = currentIndex < maximumIndex || alwaysShowTheNextButton
    const buttonNextClassnames = clsx(styles.button, styles.buttonNext, showNextButton && styles.show)

    return (
      <div className={styles.buttonContainer}>
        <div className={buttonPrevClassnames}>
          <Button
            size='Regular'
            onClick={handlePrevClick}
            aria-label={prevButtonAriaLabel.length !== 0 ? prevButtonAriaLabel : 'View the previous slide'}
            testNameSpace={`${buttonTestNameSpace}-prev`}
            tabIndex={showPrevButton ? 0 : -1}
            disabled={!showPrevButton}
          >
            <Icon.ArrowLeft size='Small' />
          </Button>
        </div>
        <div className={buttonNextClassnames}>
          <Button
            size='Regular'
            onClick={handleNextClick}
            aria-label={nextButtonAriaLabel.length !== 0 ? nextButtonAriaLabel : 'View the next slide'}
            testNameSpace={`${buttonTestNameSpace}-next`}
            tabIndex={showNextButton ? 0 : -1}
            disabled={!showNextButton}
          >
            <Icon.ArrowRight size='Small' />
          </Button>
        </div>
      </div>
    )
  }, [currentIndex, minimunIndex, maximumIndex, testNameSpace, alwaysShowTheNextButton, nextButtonAriaLabel, prevButtonAriaLabel])

  return (
    <div className={styles.transformSlideContainer}>
      {controlContainerDom != null ? createPortal(renderActionButtons(), controlContainerDom) : renderActionButtons()}
      {renderTransformSlides()}
    </div>
  )
}
